import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { t } from 'i18next';
import client from '../../service/graphql';
import { IGraphQLResponse } from '../../types/commonTypes';
import {
  GroupMember,
  IAddRemoveMembersPayload,
  ICreateUserGroupPayload,
  IModule,
  IUserGroup,
  userGroupsActions,
} from './userGroupsSlice';
import GET_USER_GROUPS, {
  ADD_REMOVE_MEMBERS,
  CREATE_USER_GROUP,
  DELETE_USER_GROUP_MUTATION,
  GET_ALL_CLIENTS_COACHES,
  GET_ALL_MODULES,
  UPDATE_USER_GROUP,
} from '../../service/graphql/userGroupsQuery';
import { toastStyles } from '../../utils/utils';
import i18n from '../../i18n';

export interface IGetUserGroupsResponse {
  getUserGroups: { totalClientCount: number; groupList: IUserGroup[] };
}

export interface IGetCochesClientsResponse {
  getCoachedWithClients: { clients: GroupMember[]; coaches: GroupMember[] };
}

export interface ICreateGroupResponse {
  createUserGroup: { id: string; name: string };
}

interface IGetModulesResponse {
  getModuleWithPermissions: IModule[];
}

function* watchFetchAllUserGroups({
  payload,
}: PayloadAction<{ skip: number; take: number; searchQuery: string; organizationId: string }>) {
  try {
    const { skip, take, searchQuery, organizationId } = payload;
    const result: IGraphQLResponse<IGetUserGroupsResponse> = yield call(client.query.bind(client), {
      query: GET_USER_GROUPS,
      variables: { skip, take, searchQuery, organizationId },
      fetchPolicy: 'no-cache',
    });

    yield put(userGroupsActions.fetchAllUserGroupsSuccess(result.data));
  } catch (error: unknown) {
    if (error instanceof Error)
      yield put(userGroupsActions.fetchAllUserGroupsFailure(error.message));
  }
}

function* watchDeleteUserGroup({ payload }: PayloadAction<string>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: DELETE_USER_GROUP_MUTATION,
      variables: { id: payload },
    });
    yield put(userGroupsActions.deleteUserGroupSuccess());
    yield put(
      userGroupsActions.fetchAllUserGroups({
        skip: 0,
        take: 10,
        searchQuery: '',
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
      })
    );
    toast.success(t('userGroups.deleteUserGroupModal.successToastMessage'), toastStyles.error);
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(userGroupsActions.deleteUserGroupFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchFetchAllModules() {
  try {
    const result: IGraphQLResponse<IGetModulesResponse> = yield call(client.query.bind(client), {
      query: GET_ALL_MODULES,
    });

    yield put(userGroupsActions.fetchAllModulesSuccess(result.data.getModuleWithPermissions));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(userGroupsActions.fetchAllModulesFailure(error.message));
  }
}

function* watchCreateUserGroup({
  payload,
}: PayloadAction<{ values: ICreateUserGroupPayload; isCreate: boolean }>) {
  try {
    if (payload.isCreate) {
      const result: IGraphQLResponse<ICreateGroupResponse> = yield call(
        client.mutate.bind(client),
        {
          mutation: CREATE_USER_GROUP,
          variables: { input: payload.values },
        }
      );
      yield put(userGroupsActions.createUserGroupSuccess(result.data.createUserGroup));
    } else {
      yield call(client.mutate.bind(client), {
        mutation: UPDATE_USER_GROUP,
        variables: { input: payload.values },
      });
      yield put(userGroupsActions.updateUserGroupSuccess());
    }

    if (!payload.isCreate)
      toast.success(
        t('userGroups.createUpdateUserGroup.updateSuccessToastMessage'),
        toastStyles.success
      );
    yield put(
      userGroupsActions.fetchAllUserGroups({
        skip: 0,
        take: 10,
        searchQuery: '',
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(userGroupsActions.createUpdateUserGroupFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchFetchAllClientsAndCooachedGroups({
  payload,
}: PayloadAction<{ organizationId: string; filterText: string }>) {
  try {
    const { organizationId, filterText } = payload;
    const result: IGraphQLResponse<IGetCochesClientsResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_ALL_CLIENTS_COACHES,
        variables: { organizationId, filterText },
      }
    );

    yield put(userGroupsActions.fetchCoachesAndClientsRequestSuccess(result.data));
  } catch (error: unknown) {
    if (error instanceof Error)
      yield put(userGroupsActions.fetchCoachesAndClientsRequestFailure(error.message));
  }
}

function* watchAddRemoveMembers({ payload }: PayloadAction<IAddRemoveMembersPayload>) {
  try {
    yield call(client.mutate.bind(client), {
      mutation: ADD_REMOVE_MEMBERS,
      variables: { groupId: payload.id, coachIds: payload.coachIds, clientIds: payload.clientIds },
    });
    yield put(userGroupsActions.addRemoveMembersSuccess());
    yield put(
      userGroupsActions.fetchAllUserGroups({
        skip: 0,
        take: 10,
        searchQuery: '',
        organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
      })
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(userGroupsActions.addRemoveMembersFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

export function* watchUserGroupsSagas() {
  yield takeLatest(userGroupsActions.fetchAllUserGroups, watchFetchAllUserGroups);
  yield takeLatest(userGroupsActions.deleteUserGroup, watchDeleteUserGroup);
  yield takeLatest(userGroupsActions.fetchAllModules, watchFetchAllModules);
  yield takeLatest(userGroupsActions.createUpdateUserGroup, watchCreateUserGroup);
  yield takeLatest(
    userGroupsActions.fetchCoachesAndClientsRequest,
    watchFetchAllClientsAndCooachedGroups
  );
  yield takeLatest(userGroupsActions.addRemoveMembers, watchAddRemoveMembers);
}

const userGroupsSaga = watchUserGroupsSagas;
export default userGroupsSaga;
